import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { useToast } from '../store/ToastContext';
import { format } from 'date-fns';
import {
    addExpense, subscribeToGroupExpenses, updateExpense, deleteExpense
} from '../services/expenses';
import {
    addTask, toggleTask, deleteTask, subscribeToGroupTasks
} from '../services/tasks';
import {
    getGroupById, subscribeToGroup
} from '../services/groups';
import { getMultipleUserProfiles } from '../services/users';
import { convertCurrency } from '../services/currency';
import type { Group, Expense, TodoTask, UserProfile } from '../types';

export type TabType = 'expenses' | 'tasks' | 'members';
export type SplitMode = 'equal' | 'exact' | 'percentage';

export const useGroupDetails = (groupId: string | undefined) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showToast } = useToast();

    const [group, setGroup] = useState<Group | null>(null);
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [tasks, setTasks] = useState<TodoTask[]>([]);
    const [memberProfiles, setMemberProfiles] = useState<UserProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<TabType>('expenses');

    // Confirm dialog state
    const [isDeleteExpenseDialogOpen, setIsDeleteExpenseDialogOpen] = useState(false);

    // Expense Form State
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [expenseCurrency, setExpenseCurrency] = useState('');
    const [expenseDate, setExpenseDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
    const [splitMode, setSplitMode] = useState<SplitMode>('equal');
    const [exactAmounts, setExactAmounts] = useState<Record<string, string>>({});
    const [percentages, setPercentages] = useState<Record<string, string>>({});

    const [newTaskTitle, setNewTaskTitle] = useState('');

    // ── Initialization & Subscriptions ────────────────────────────────────────

    useEffect(() => {
        if (!groupId || !user) return;

        // Initial Fetch for profiles & settings
        const init = async () => {
            try {
                const groupData = await getGroupById(groupId);
                if (groupData) {
                    // Check if user is member
                    if (!groupData.members.includes(user.uid)) {
                        showToast("You are not a member of this group", "error");
                        void navigate('/');
                        return;
                    }

                    const profiles = await getMultipleUserProfiles(groupData.members);
                    setMemberProfiles(profiles);
                    setExpenseCurrency(groupData.currency);
                    setSelectedMemberIds(groupData.members);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        void init();

        // Subscriptions
        const unsubGroup = subscribeToGroup(groupId, (updatedGroup: Group | null) => {
            if (updatedGroup) setGroup(updatedGroup);
        });

        const unsubExpenses = subscribeToGroupExpenses(groupId, (updatedExpenses: Expense[]) => {
            setExpenses(updatedExpenses);
        });

        const unsubTasks = subscribeToGroupTasks(groupId, (updatedTasks: TodoTask[]) => {
            setTasks(updatedTasks);
        });

        return () => {
            unsubGroup();
            unsubExpenses();
            unsubTasks();
        };
    }, [groupId, user, navigate, showToast]);

    // If group members change, refresh profiles
    useEffect(() => {
        if (group?.members) {
            void getMultipleUserProfiles(group.members).then(setMemberProfiles);
        }
    }, [group?.members]);

    // ── Logic: Expense Management ─────────────────────────────────────────────

    const resetExpenseForm = () => {
        setEditingExpense(null);
        setDescription('');
        setAmount('');
        setExactAmounts({});
        setPercentages({});
        setSplitMode('equal');
        if (group) {
            setExpenseCurrency(group.currency);
            setSelectedMemberIds(group.members);
            setExpenseDate(format(new Date(), 'yyyy-MM-dd'));
        }
    };

    const handleAddExpense = async (e?: React.SyntheticEvent<HTMLFormElement>) => {
        if (e) e.preventDefault();
        if (!user || !group || !groupId) return;

        if (!description.trim()) {
            showToast("Please enter a description", "error");
            return;
        }

        try {
            const numTotalAmount = parseFloat(amount);
            if (isNaN(numTotalAmount) || numTotalAmount <= 0) {
                showToast("Please enter a valid positive amount", "error");
                return;
            }

            if (selectedMemberIds.length === 0) {
                showToast("Select at least one member to split with", "error");
                return;
            }

            let convertedTotal = numTotalAmount;
            if (expenseCurrency !== group.currency) {
                convertedTotal = await convertCurrency(numTotalAmount, expenseCurrency, group.currency, expenseDate);
            }

            // Calculate splits
            const splitWith = selectedMemberIds.map(uid => {
                let memberAmount = 0;
                let memberPercentage = 0;

                if (splitMode === 'equal') {
                    memberAmount = convertedTotal / selectedMemberIds.length;
                    memberPercentage = 100 / selectedMemberIds.length;
                } else if (splitMode === 'exact') {
                    const val = parseFloat(exactAmounts[uid] ?? '0');
                    memberAmount = expenseCurrency !== group.currency
                        ? (val / numTotalAmount) * convertedTotal
                        : val;
                    memberPercentage = (memberAmount / convertedTotal) * 100;
                } else {
                    memberPercentage = parseFloat(percentages[uid] ?? '0');
                    memberAmount = (memberPercentage / 100) * convertedTotal;
                }

                return { userId: uid, amount: memberAmount, percentage: memberPercentage };
            });

            // Validation
            if (splitMode === 'exact') {
                const totalExact = Object.values(exactAmounts).reduce((sum, v) => sum + parseFloat(v || '0'), 0);
                if (Math.abs(totalExact - numTotalAmount) > 0.01) {
                    showToast(`Sum of amounts (${totalExact.toFixed(2)}) must equal total (${numTotalAmount.toFixed(2)})`, "error");
                    return;
                }
            } else if (splitMode === 'percentage') {
                const totalPerc = Object.values(percentages).reduce((sum, v) => sum + parseFloat(v || '0'), 0);
                if (Math.abs(totalPerc - 100) > 0.1) {
                    showToast(`Percentages must sum to 100% (currently ${totalPerc.toFixed(1)}%)`, "error");
                    return;
                }
            }

            const expenseData: Omit<Expense, 'id' | 'createdAt'> = {
                groupId,
                description,
                amount: convertedTotal,
                currency: group.currency,
                originalAmount: numTotalAmount,
                originalCurrency: expenseCurrency,
                payerId: editingExpense ? editingExpense.payerId : user.uid,
                date: expenseDate,
                splitWith
            };

            if (editingExpense) {
                await updateExpense(editingExpense.id, expenseData);
                showToast("Expense updated!", "success");
            } else {
                await addExpense(expenseData);
                showToast("Expense added!", "success");
            }

            setIsAddModalOpen(false);
            resetExpenseForm();
        } catch {
            showToast("Failed to save expense", "error");
        }
    };

    const handleEditClick = (expense: Expense) => {
        setEditingExpense(expense);
        setDescription(expense.description);
        setAmount((expense.originalAmount ?? expense.amount).toString());
        setExpenseCurrency(expense.originalCurrency ?? expense.currency);
        setExpenseDate(expense.date);
        setSelectedMemberIds(expense.splitWith.map(s => s.userId));

        // Attempt to determine split mode
        const isEqual = expense.splitWith.every((s, _, arr) => Math.abs(s.percentage - (100 / arr.length)) < 0.1);

        if (isEqual) {
            setSplitMode('equal');
        } else {
            // Determine if it was exact or percentage based on history is hard, 
            // but defaulting to 'exact' with pre-filled amounts is safer UI wise
            // actually let's try to detect if percentages look "clean"?
            // For now, let's default to 'exact' if not equal, as it gives most control.
            setSplitMode('exact');
            const amounts: Record<string, string> = {};
            const percs: Record<string, string> = {};

            // We need to reverse calculate original amounts if currency differed
            // But we stored originalAmount on expense level, not per split.
            // So we approximate based on percentage of original total.
            const originalTotal = expense.originalAmount ?? expense.amount;

            expense.splitWith.forEach(s => {
                const shareOfTotal = s.percentage / 100;
                const originalShare = originalTotal * shareOfTotal;
                amounts[s.userId] = originalShare.toFixed(2);
                percs[s.userId] = s.percentage.toFixed(1);
            });
            setExactAmounts(amounts);
            setPercentages(percs);
        }

        setIsAddModalOpen(true);
    };

    const handleDeleteExpenseClick = () => {
        setIsDeleteExpenseDialogOpen(true);
    };

    const handleDeleteExpenseConfirm = async () => {
        if (!editingExpense) return;
        try {
            await deleteExpense(editingExpense.id);
            showToast("Expense deleted", "info");
            setIsAddModalOpen(false);
            resetExpenseForm();
            setIsDeleteExpenseDialogOpen(false); // Ensure dialog closes
        } catch {
            showToast("Failed to delete expense", "error");
        }
    };

    const toggleMemberSelection = (uid: string) => {
        setSelectedMemberIds(prev =>
            prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
        );
    };

    // ── Logic: Tasks ──────────────────────────────────────────────────────────

    const handleAddTask = async (e?: React.SyntheticEvent<HTMLFormElement>) => {
        if (e) e.preventDefault();
        if (!user || !groupId || !newTaskTitle) return;
        try {
            await addTask({
                groupId,
                title: newTaskTitle,
                completed: false,
                createdBy: user.uid
            });
            setNewTaskTitle('');
            showToast("Task added!", "success");
        } catch {
            showToast("Failed to add task", "error");
        }
    };

    const handleToggleTask = async (taskId: string, completed: boolean) => {
        try {
            await toggleTask(taskId, completed);
        } catch {
            showToast("Failed to update task", "error");
        }
    };

    const handleDeleteTask = async (taskId: string) => {
        try {
            await deleteTask(taskId);
            showToast("Task deleted", "info");
        } catch {
            showToast("Failed to delete task", "error");
        }
    };

    // ── Derived Data ──────────────────────────────────────────────────────────

    const totalSpending = useMemo(() => {
        return expenses.reduce((sum, e) => sum + e.amount, 0);
    }, [expenses]);

    const myBalance = useMemo(() => {
        if (!user) return 0;
        const paidByMe = expenses.filter(e => e.payerId === user.uid).reduce((sum, e) => sum + e.amount, 0);
        const myShare = expenses.reduce((sum, e) => {
            const split = e.splitWith.find(s => s.userId === user.uid);
            return sum + (split?.amount ?? 0);
        }, 0);
        return paidByMe - myShare;
    }, [expenses, user]);

    return {
        // Data
        group,
        expenses,
        tasks,
        memberProfiles,
        loading,
        currUser: user,
        activeTab, setActiveTab,
        totalSpending,
        myBalance,

        // Form States - Add Expense
        isAddModalOpen, setIsAddModalOpen,
        editingExpense,
        description, setDescription,
        amount, setAmount,
        expenseCurrency, setExpenseCurrency,
        expenseDate, setExpenseDate,
        selectedMemberIds, setSelectedMemberIds,
        splitMode, setSplitMode,
        exactAmounts, setExactAmounts,
        percentages, setPercentages,

        // Form States - Tasks
        newTaskTitle, setNewTaskTitle,

        // Dialog States
        isDeleteExpenseDialogOpen, setIsDeleteExpenseDialogOpen,

        // Actions
        handleAddExpense,
        handleEditClick,
        handleDeleteExpenseClick,
        handleDeleteExpenseConfirm,
        resetExpenseForm,
        toggleMemberSelection,
        handleAddTask,
        handleToggleTask,
        handleDeleteTask,
    };
};
